/**
 * AssignContentDialog
 *
 * Quick "assign a course to this user" flow surfaced from any row-
 * action menu that lists users (Users page, group Learners table,
 * etc.). Reuses the same `assignmentDefinitionsClient.createDefinition`
 * endpoint as the full Assignments page — just a streamlined modal so
 * admins don't have to context-switch.
 *
 * Targets are passed in as `targetUsers`, so the same component drops
 * into any surface that already has the user(s) selected.
 */

import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { AssignmentDefinition, Course, Login } from '../../apis/api-client';
import { assignmentDefinitionsClient } from '../../apis/client-instances';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '../ui';
import { CourseAutocomplete } from '../common';
import { alert } from '../../hooks/function';
import { getUserDisplayName } from '../../utils/roleHelpers';

export interface AssignContentDialogProps {
  open: boolean;
  onClose: () => void;
  /** The user(s) the new assignment will target. Most callers pass one;
   *  the component handles N for future bulk usage. */
  targetUsers: Login[];
  /** Fires after the assignment is created so the parent can refresh
   *  state if it wants (the row table doesn't need to). */
  onAssigned?: (definition: AssignmentDefinition) => void;
}

export default function AssignContentDialog({
  open,
  onClose,
  targetUsers,
  onAssigned,
}: AssignContentDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [course, setCourse] = useState<Course | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [deadline, setDeadline] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Reset the form whenever the dialog reopens — admins routinely
  // assign different content to different users back-to-back, and
  // sticky state from the last open would surprise them.
  useEffect(() => {
    if (open) {
      setCourse(null);
      setStartDate('');
      setDeadline('');
    }
  }, [open]);

  const targetUserIds = targetUsers.map((u) => u.id!).filter(Boolean);

  // Pretty target list — at most two names, then "+ N more" for any
  // bulk overflow. The dialog title bar is the main affordance for
  // "who's being assigned to", so we keep the copy compact.
  const targetSummary =
    targetUsers.length === 0
      ? 'No learners selected'
      : targetUsers.length <= 2
        ? targetUsers.map(getUserDisplayName).join(', ')
        : `${targetUsers.slice(0, 2).map(getUserDisplayName).join(', ')} + ${targetUsers.length - 2} more`;

  const handleAssign = async () => {
    if (!course?.id) {
      alert(enqueueSnackbar, 'Pick a course, learning path, or event to assign.', 'warning');
      return;
    }
    if (!deadline) {
      alert(enqueueSnackbar, 'Set a deadline for the assignment.', 'warning');
      return;
    }
    if (targetUserIds.length === 0) {
      alert(enqueueSnackbar, 'No target learner.', 'error');
      return;
    }
    setSaving(true);
    try {
      const def = await assignmentDefinitionsClient.createDefinition({
        courseId: course.id,
        targetUserIds,
        startDate: startDate ? new Date(startDate) : undefined,
        deadline: new Date(deadline),
      } as AssignmentDefinition);
      alert(
        enqueueSnackbar,
        targetUsers.length === 1
          ? `Assigned to ${getUserDisplayName(targetUsers[0])}`
          : `Assigned to ${targetUsers.length} learners`,
        'success',
      );
      onAssigned?.(def);
      onClose();
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to create assignment', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          Assign content
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Assigning to {targetSummary}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {/* Content picker — courses, learning paths, and events all
              come back from the same endpoint; the type chip on each
              option helps admins differentiate. */}
          <CourseAutocomplete
            value={course}
            onChange={(v) => setCourse(Array.isArray(v) ? v[0] ?? null : v)}
            placeholder="Search content…"
            label="Course, Learning Path, or Event"
          />
          {/* Two-column dates. Start is optional (defaults to "now" on
              the server when omitted); deadline is required so the
              admin can't accidentally create an open-ended assignment. */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              type="date"
              label="Start (optional)"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              type="date"
              label="Deadline"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            The assigned learner{targetUsers.length === 1 ? '' : 's'} get an enrollment created automatically and receive the standard assignment notification.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!course || !deadline || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            color: '#fff',
            '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
            px: 3,
          }}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
